import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { DrawnSignatureCapture } from './DrawnSignatureCapture'
import type { ElectronicSignatureMode, BiometricSample } from '../types'

interface Props {
  mode: ElectronicSignatureMode
  onModeChange: (m: ElectronicSignatureMode) => void
  signerName: string
  onSignerNameChange: (s: string) => void
  shortId: string
  onQrChange: (dataUrl: string | null) => void
  onDrawnChange: (out: { imageDataUrl: string | null; biometricData: BiometricSample[] }) => void
}

const MODE_OPTIONS: { v: ElectronicSignatureMode; l: string }[] = [
  { v: 'text', l: 'Texto cursivo' },
  { v: 'drawn', l: 'Dibujada (mano alzada)' },
  { v: 'qr', l: 'Solo QR' },
  { v: 'text_and_qr', l: 'Texto + QR' },
  { v: 'drawn_and_qr', l: 'Dibujada + QR' },
]

export function SignatureContentEditor({
  mode, onModeChange,
  signerName, onSignerNameChange,
  shortId, onQrChange, onDrawnChange,
}: Props) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  const showText = mode === 'text' || mode === 'text_and_qr'
  const showDrawn = mode === 'drawn' || mode === 'drawn_and_qr'
  const showQr = mode === 'qr' || mode === 'text_and_qr' || mode === 'drawn_and_qr'

  useEffect(() => {
    if (!showQr) {
      setQrDataUrl(null)
      onQrChange(null)
      return
    }
    const url = `${window.location.origin}/v/${shortId}`
    QRCode.toDataURL(url, { width: 320, margin: 1, errorCorrectionLevel: 'M' })
      .then(d => { setQrDataUrl(d); onQrChange(d) })
      .catch(() => { setQrDataUrl(null); onQrChange(null) })
  }, [showQr, shortId, onQrChange])

  return (
    <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md border border-legal-100 dark:border-legal-700 p-6">
      <h2 className="text-lg font-bold text-legal-900 dark:text-white mb-1">2. Contenido de la firma</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Elegí qué aparece dentro del rectángulo de firma
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {MODE_OPTIONS.map(opt => (
          <button
            key={opt.v}
            onClick={() => onModeChange(opt.v)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              mode === opt.v
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'bg-white dark:bg-legal-700 border-legal-200 dark:border-legal-600 text-legal-700 dark:text-legal-200 hover:border-primary-400'
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {showText && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
            Nombre a firmar
          </label>
          <input
            type="text"
            value={signerName}
            onChange={(e) => onSignerNameChange(e.target.value)}
            placeholder="Ej: Daniel Arias"
            className="w-full px-4 py-2.5 rounded-lg border border-legal-200 dark:border-legal-600 bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
          />
        </div>
      )}

      {showDrawn && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-2">
            Tu firma dibujada
          </label>
          <DrawnSignatureCapture onChange={onDrawnChange} />
        </div>
      )}

      <div className="bg-legal-50 dark:bg-legal-900/40 border border-legal-200 dark:border-legal-700 rounded-lg p-4">
        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Vista previa</p>
        <div className="flex items-center gap-4 min-h-[80px]">
          {showText && (
            <div
              className="text-2xl text-legal-900 dark:text-white flex-1"
              style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}
            >
              {signerName || 'Tu nombre aquí'}
            </div>
          )}
          {showDrawn && (
            <div className="flex-1 text-xs text-gray-500 dark:text-gray-400 italic">
              (la imagen dibujada arriba se va a embeber)
            </div>
          )}
          {showQr && qrDataUrl && (
            <img src={qrDataUrl} alt="QR" className="w-20 h-20 bg-white p-1 rounded" />
          )}
          {showQr && !qrDataUrl && (
            <div className="w-20 h-20 bg-legal-200 dark:bg-legal-700 rounded animate-pulse" />
          )}
        </div>
      </div>
    </div>
  )
}
